// Pure helpers for the "Import your rider (AI)" flow — turning the AI's
// `proposedFields` payload (sections → transports → fields) into something a
// human can review BEFORE anything is written. Kept dependency-free and
// side-effect-free so it is unit-testable outside React.

// The 10 sections the /api/ai/assist `import_rider` task can propose, in the
// order they appear in the rider PDF (mirrors server SECTION_SCHEMA).
export const AI_SECTIONS = [
  'sound',
  'iem',
  'stage',
  'lights',
  'backline',
  'stageDesign',
  'hospitality',
  'specs',
  'general',
] as const;

// The four transport presets each section can carry. The LLM only emits a
// transport key when the rider mentions it explicitly.
export const AI_TRANSPORTS = ['van', 'plane', 'tourbus', 'train'] as const;

export const AI_SECTION_LABELS: Record<string, string> = {
  sound: 'Sound',
  iem: 'In-ear Monitors (IEM)',
  stage: 'Stage',
  lights: 'Lights',
  backline: 'Backline',
  stageDesign: 'Stage Design',
  hospitality: 'Hospitality',
  specs: 'Specs',
  general: 'General',
};

export const AI_TRANSPORT_LABELS: Record<string, string> = {
  van: 'Van',
  plane: 'Plane',
  tourbus: 'Tour Bus',
  train: 'Train',
};

export interface SummaryField {
  key: string;
  value: string;
}

export interface SummaryTransport {
  transport: string;
  fields: SummaryField[];
}

export interface SummarySection {
  section: string;
  transports: SummaryTransport[];
}

/** Render any proposed value as a compact, human-readable string.
 * Returns null for empty / undefined / null values so empty fields never
 * clutter the preview. */
export function stringifyAiValue(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  if (Array.isArray(value)) {
    const items = value
      .map((x) => (typeof x === 'object' && x !== null ? JSON.stringify(x) : String(x)))
      .filter((s) => s.trim().length > 0);
    return items.length > 0 ? items.join(', ') : null;
  }
  if (typeof value === 'object') {
    const json = JSON.stringify(value);
    return json && json !== '{}' && json !== '[]' ? json : null;
  }
  const s = String(value).trim();
  return s.length > 0 ? s : null;
}

/**
 * Flatten `proposedFields` into reviewable sections. Sections with no
 * non-empty content are omitted; transports and fields follow the AI's own
 * key order so the preview reads top-to-bottom like the rider.
 */
export function buildProposalSummary(proposedFields: unknown): SummarySection[] {
  if (!proposedFields || typeof proposedFields !== 'object' || Array.isArray(proposedFields)) {
    return [];
  }
  const src = proposedFields as Record<string, unknown>;
  const out: SummarySection[] = [];

  for (const section of AI_SECTIONS) {
    const sec = src[section];
    if (!sec || typeof sec !== 'object' || Array.isArray(sec)) continue;
    const transports: SummaryTransport[] = [];

    for (const transport of AI_TRANSPORTS) {
      const t = (sec as Record<string, unknown>)[transport];
      if (!t || typeof t !== 'object' || Array.isArray(t)) continue;
      const fields: SummaryField[] = [];
      for (const [key, value] of Object.entries(t as Record<string, unknown>)) {
        const rendered = stringifyAiValue(value);
        if (rendered === null) continue;
        fields.push({ key, value: rendered });
      }
      if (fields.length > 0) transports.push({ transport, fields });
    }

    if (transports.length > 0) out.push({ section, transports });
  }

  return out;
}

/** Count of sections with at least one non-empty mapped field. */
export function countMappedSections(proposedFields: unknown): number {
  return buildProposalSummary(proposedFields).length;
}

/** Total number of non-empty fields across every mapped section. */
export function countMappedFields(proposedFields: unknown): number {
  return buildProposalSummary(proposedFields).reduce(
    (n, s) => n + s.transports.reduce((m, t) => m + t.fields.length, 0),
    0,
  );
}